import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Clock, MessageCircle, Flame, Trophy, Star, 
  TrendingUp, BookOpen, Target, Heart, ArrowRight,
  Volume2, Sparkles, ChevronRight, Home
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import confetti from 'canvas-confetti';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Lira avatars
const LIRA_AVATARS = {
  female: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face',
  male: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face'
};

// Relationship level colors
const RELATIONSHIP_COLORS = {
  new: 'from-blue-400 to-blue-600',
  acquaintance: 'from-green-400 to-green-600',
  friend: 'from-purple-400 to-purple-600',
  close_friend: 'from-pink-400 to-pink-600',
  best_friend: 'from-amber-400 to-amber-600'
};

// Animated counter component
const AnimatedNumber = ({ value, suffix = '', duration = 1000 }) => {
  const [display, setDisplay] = useState(0);
  
  useEffect(() => {
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value, duration]);
  
  return <span>{display}{suffix}</span>;
};

const SessionEvolution = () => {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [evolutionData, setEvolutionData] = useState(null);
  const [voiceGender, setVoiceGender] = useState('female');
  const [isLiraSpeaking, setIsLiraSpeaking] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    fetchEvolution();
    fetchVoicePreference();
  }, [conversationId]);

  const fetchVoicePreference = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/personality`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoiceGender(response.data.voice_gender || 'female');
    } catch (error) {
      console.error('Failed to fetch voice preference:', error);
    }
  };

  const fetchEvolution = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/companion/session/end`,
        { conversation_id: conversationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setEvolutionData(response.data);
      
      // Trigger confetti for good sessions
      const duration = response.data.evolution?.session?.duration_minutes || 0;
      if (duration >= 5) {
        setTimeout(() => {
          setShowConfetti(true);
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        }, 500);
      }
      
      // Speak farewell after short delay
      setTimeout(() => {
        if (response.data.session_summary?.farewell) {
          speakFarewell(response.data.session_summary.farewell);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Failed to fetch evolution:', error);
      toast.error('Não foi possível carregar o resumo.');
    } finally {
      setLoading(false);
    }
  };

  const speakFarewell = (text) => {
    const mainText = text.split('\n(')[0].split('(')[0].trim();
    if (!mainText) return;

    setIsLiraSpeaking(true);
    try {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(mainText);
        utterance.lang = user?.target_language === 'Spanish' ? 'es-ES' : 
                        user?.target_language === 'French' ? 'fr-FR' :
                        user?.target_language === 'German' ? 'de-DE' : 'en-US';
        utterance.rate = 0.85;
        utterance.onend = () => setIsLiraSpeaking(false);
        utterance.onerror = () => setIsLiraSpeaking(false);
        window.speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsLiraSpeaking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sand to-[#F5EDE4] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-lime/30 border-t-lime rounded-full mx-auto mb-4"
          />
          <p className="text-forest">Calculando sua evolução...</p>
        </motion.div>
      </div>
    );
  }

  const evolution = evolutionData?.evolution || {};
  const session = evolution.session || {};
  const progress = evolution.progress || {};
  const relationship = evolution.relationship || {};
  const farewell = evolutionData?.session_summary?.farewell || '';
  const encouragement = evolutionData?.encouragement || '';
  const nextHint = evolutionData?.next_session_hint || '';

  // Parse farewell for translation
  const farewellParts = farewell.split('\n(');
  const farewellMain = farewellParts[0] || farewell;
  const farewellTranslation = farewellParts[1]?.replace(')', '') || '';

  // Determine achievement level
  const achievementLevel = session.duration_minutes >= 10 ? 'amazing' :
                          session.duration_minutes >= 5 ? 'great' :
                          session.duration_minutes >= 2 ? 'good' : 'start';

  const achievementEmoji = {
    amazing: '🏆',
    great: '🌟',
    good: '✨',
    start: '🌱'
  };

  const achievementText = {
    amazing: 'Sessão Incrível!',
    great: 'Ótima Prática!',
    good: 'Bom Progresso!',
    start: 'Bom Começo!'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-forest via-emerald-800 to-forest" data-testid="session-evolution">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} />
      </div>

      <main className="relative px-6 py-8 max-w-lg mx-auto">
        
        {/* Achievement Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="text-6xl mb-4"
          >
            {achievementEmoji[achievementLevel]}
          </motion.div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {achievementText[achievementLevel]}
          </h1>
          <p className="text-white/70">
            Sessão encerrada • {new Date().toLocaleDateString('pt-BR', { weekday: 'long' })}
          </p>
        </motion.div>

        {/* Main Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-3xl p-6 shadow-xl mb-6"
        >
          {/* Duration Highlight */}
          <div className="text-center mb-6 pb-6 border-b border-forest/10">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Clock className="w-6 h-6 text-lime" />
              <span className="text-5xl font-bold text-forest">
                <AnimatedNumber value={Math.round(session.duration_minutes || 0)} />
              </span>
              <span className="text-2xl text-forest/70">min</span>
            </div>
            <p className="text-stone text-sm">de conversa com a Lira</p>
            {evolution.comparison && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="text-lime text-sm mt-2 font-medium"
              >
                {evolution.comparison}
              </motion.p>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <MessageCircle className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-forest">
                <AnimatedNumber value={session.your_messages || 0} />
              </p>
              <p className="text-xs text-stone">suas mensagens</p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Flame className="w-6 h-6 text-orange-500" />
              </div>
              <p className="text-2xl font-bold text-forest">
                <AnimatedNumber value={relationship.consecutive_days || 0} />
              </p>
              <p className="text-xs text-stone">dias seguidos</p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Clock className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-forest">
                <AnimatedNumber value={Math.round((relationship.total_hours || 0) * 10) / 10} suffix="h" />
              </p>
              <p className="text-xs text-stone">total praticado</p>
            </div>
          </div>

          {/* Topics & Vocabulary */}
          {(progress.topics_practiced?.length > 0 || progress.vocabulary_used?.length > 0) && (
            <div className="space-y-4 pt-4 border-t border-forest/10">
              {progress.topics_practiced?.length > 0 && (
                <div>
                  <p className="text-xs text-stone uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Target className="w-3 h-3" />
                    Tópicos praticados
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {progress.topics_practiced.map((topic, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        className="bg-lime/20 text-forest text-sm px-3 py-1 rounded-full"
                      >
                        {topic}
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}

              {progress.vocabulary_used?.length > 0 && (
                <div>
                  <p className="text-xs text-stone uppercase tracking-wider mb-2 flex items-center gap-2">
                    <BookOpen className="w-3 h-3" />
                    Vocabulário usado
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {progress.vocabulary_used.map((word, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.6 + i * 0.1 }}
                        className="bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-full font-medium"
                      >
                        {word}
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* Strengths & Improvements */}
        {(progress.strengths?.length > 0 || progress.to_improve?.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="grid grid-cols-2 gap-4 mb-6"
          >
            {progress.strengths?.length > 0 && (
              <div className="bg-lime/20 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-lime" />
                  <p className="text-sm font-medium text-forest">Pontos Fortes</p>
                </div>
                <ul className="space-y-2">
                  {progress.strengths.slice(0, 3).map((strength, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + i * 0.1 }}
                      className="text-sm text-forest/80 flex items-start gap-2"
                    >
                      <span className="text-lime">✓</span>
                      <span>{strength}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            )}

            {progress.to_improve?.length > 0 && (
              <div className="bg-amber-50 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                  <p className="text-sm font-medium text-forest">Próximo Foco</p>
                </div>
                <ul className="space-y-2">
                  {progress.to_improve.slice(0, 3).map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + i * 0.1 }}
                      className="text-sm text-forest/80 flex items-start gap-2"
                    >
                      <span className="text-amber-500">→</span>
                      <span>{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}

        {/* Lira's Farewell */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-3xl p-6 shadow-xl mb-6"
        >
          <div className="flex items-start gap-4">
            <motion.div
              animate={isLiraSpeaking ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.5, repeat: isLiraSpeaking ? Infinity : 0 }}
              className="relative flex-shrink-0"
            >
              <img 
                src={LIRA_AVATARS[voiceGender]}
                alt="Lira"
                className="w-14 h-14 rounded-full object-cover border-2 border-lime"
              />
              {isLiraSpeaking && (
                <motion.div
                  className="absolute -bottom-1 -right-1 bg-lime rounded-full p-1.5"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.3, repeat: Infinity }}
                >
                  <Volume2 className="w-3 h-3 text-forest" />
                </motion.div>
              )}
            </motion.div>
            
            <div className="flex-1">
              <p className="text-forest text-lg leading-relaxed mb-2">
                {farewellMain}
              </p>
              {farewellTranslation && (
                <p className="text-stone text-sm italic">
                  ({farewellTranslation})
                </p>
              )}
              
              <button
                onClick={() => speakFarewell(farewell)}
                disabled={isLiraSpeaking}
                className="flex items-center gap-2 text-sm text-forest/60 hover:text-forest mt-3 transition-colors"
              >
                <Volume2 className={`w-4 h-4 ${isLiraSpeaking ? 'text-lime animate-pulse' : ''}`} />
                <span>{isLiraSpeaking ? 'Falando...' : 'Ouvir'}</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Encouragement */}
        {encouragement && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-center mb-6"
          >
            <p className="text-white/90 text-lg font-medium">
              {encouragement}
            </p>
          </motion.div>
        )}

        {/* Next Session Hint */}
        {nextHint && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-8 border border-white/20"
          >
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-lime" />
              <p className="text-white/90 text-sm">{nextHint}</p>
            </div>
          </motion.div>
        )}

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="space-y-3"
        >
          <Button
            onClick={() => navigate('/home')}
            className="w-full bg-lime hover:bg-lime/90 text-forest rounded-2xl py-6 text-lg font-semibold shadow-lg"
            data-testid="go-home-btn"
          >
            <Home className="w-5 h-5 mr-2" />
            Voltar para Casa da Lira
          </Button>

          <Button
            onClick={() => navigate('/conversations')}
            variant="ghost"
            className="w-full text-white/70 hover:text-white hover:bg-white/10 rounded-2xl py-4"
            data-testid="view-history-btn"
          >
            Ver histórico de conversas
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>

        {/* Relationship Progress (subtle) */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="mt-8 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-white/50 text-sm">
            <Heart className="w-4 h-4" />
            <span>Nível de amizade: {relationship.level || 'new'}</span>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default SessionEvolution;
